import React, { useState, useEffect, useRef, createContext } from 'react';
import {
  Avatar,
  Divider,
  Layout,
  Menu,
  Spin,
  Tooltip,
  Typography,
  message,
  notification,
} from 'antd';
import { Outlet, Link, useNavigate } from 'react-router-dom';

import {
  ProfileFilled,
  FileTextFilled,
  DollarCircleFilled,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  FolderOpenOutlined,
  AuditOutlined,
} from '@ant-design/icons';
import './Navigation.css';
import Color from '../../../constants/ColourConstants';
import Title from 'antd/es/typography/Title';
import axios from 'axios';
import { BE_DOMAIN, goToLogin } from '../../../constants/ApiConstants';
import { Footer } from 'antd/es/layout/layout';
import Icon from '@ant-design/icons/lib/components/Icon';
import CustomButton from '../Button/Button';
import AsyncTasksModal from '../../pages/BatchProcesses/AsyncTasksModal';
import { fetchCountries } from '../../../services/commonAPI';

const { Header, Sider, Content } = Layout;
export const AppFactsContext = createContext({});
export const ActionContext = createContext({});
export const CountryContext = createContext({});
export const PermissionsContext = createContext({});
const { Text } = Typography;

const Navigation = () => {
  const [collapsed, setCollapsed] = useState(true);
  const [selectedKey, setSelectedKey] = useState(window.location.pathname);
  const [authenticated, setAuthenticated] = useState(null);
  const [name, setName] = useState('');
  const [appFacts, setAppFacts] = useState({});
  const [isAsyncTasksModalOpen, setIsAsyncTasksModalOpen] = useState(false);
  const initalNotifyRef = useRef(true);
  const [notify, setNotify] = useState(null);
  const [action, setAction] = useState(false);
  const [countries, setCountries] = useState({});
  const [countryCode, setCountryCode] = useState({});
  const [permissions, setPermissions] = useState([]);
  const [api, notificationContextHolder] = notification.useNotification();

  const navigate = useNavigate();
  const socketRef = useRef(null);
  useEffect(() => {
    if (initalNotifyRef.current && appFacts) {
      openNotification(appFacts, true);
      initalNotifyRef.current = false;
    }
    if (notify && !action) {
      openNotification(notify, false);
    }
  }, [notify, appFacts]);

  const actionLabels = {
    generate_transactions: 'Create New Transaction',
    update_transactions: 'Update Transactions',
    generate_invoices: 'Create Invoice',
    update_invoices: 'Update Invoices',
    generate_payouts: 'Create Payouts',
    update_payouts: 'Update Payouts',
    regenerate_payouts: 'Regenerate Payouts',
  };

  const openNotification = (value, inital) => {
    let description = null;
    if (inital) {
      const allActions = Object.keys(value);
      const lockedActions = allActions
        .filter((key) => value[key])
        .map((key) => actionLabels[key] || key);
      const unlockedActions = allActions
        .filter((key) => !value[key])
        .map((key) => actionLabels[key] || key);
      description = (
        <>
          {lockedActions.length > 0 && (
            <div>
              <Text>
                <b>Unable to perform these actions:</b>{' '}
                {lockedActions.join(', ')}
              </Text>
            </div>
          )}
          {lockedActions.length > 0 && unlockedActions.length > 0 && <br />}
          {unlockedActions.length > 0 && (
            <Text>
              <b> You can perform the batch jobs:</b>{' '}
              {unlockedActions.join(', ')}.
            </Text>
          )}
        </>
      );
    } else {
      const check = appFacts[value];
      const name = actionLabels[value];
      description = check ? (
        <Text>
          This action is locked <b>{name}</b> because of performing{' '}
          <b>another batch job</b>.
        </Text>
      ) : (
        <Text>
          This action is unlocked <b>{name}</b> you can{' '}
          <b>perform the action</b>
        </Text>
      );
    }
    api.info({
      message: `Notification`,
      description: description,
      showProgress: true,
    });
  };

  useEffect(() => {
    // Connect to WebSocket
    const socket_uri = BE_DOMAIN.replace(/(^\w+:|^)\/\//, '');
    const hostname = window.location.hostname;
    socketRef.current = new WebSocket(
      hostname === 'localhost'
        ? `ws://${socket_uri}/ws/data`
        : `wss://${socket_uri}/ws/data`,
    );

    socketRef.current.onopen = () => {
      console.log('WebSocket Connected');
      initalNotifyRef.current = true;
    };

    socketRef.current.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        setAppFacts((prevFacts) => {
          const updatedFacts = message.reduce((acc, item) => {
            const value = item.value === 'TRUE';
            const key = item?.key;
            if (prevFacts[key] !== value && !initalNotifyRef.current) {
              setNotify(key);
            }

            acc[key] = value;
            return acc;
          }, {});

          return updatedFacts;
        });
      } catch (err) {
        console.error('Invalid WebSocket message', err);
      }
    };

    socketRef.current.onclose = () => {
      console.log('WebSocket Disconnected');
    };

    return () => {
      socketRef.current.close();
    };
  }, []);

  useEffect(() => {
    if (window.location.hostname === 'localhost') {
      getCountries();
      setAuthenticated(true);
    } else {
      axios
        .get(BE_DOMAIN + '/session', {
          withCredentials: true,
        })
        .then((res) => {
          if (res.data.authenticated == true && !authenticated) {
            setAuthenticated(true);
            getCountries();
            setName(res.data.currentUser || '');
            setPermissions(res.data.permissions || []);
          } else {
            setAuthenticated(false);
            goToLogin();
          }
        })
        .catch(() => {
          setAuthenticated(false);
          goToLogin();
        });
    }

    if (window.location.pathname === '/') {
      setSelectedKey('/transactions');
      navigate('/transactions');
    }
  }, []);

  useEffect(() => {
    setSelectedKey(window.location.pathname);
  }, [window.location.pathname]);

  const getCountries = () => {
    fetchCountries()
      .then((res) => {
        if (res && res.length > 0) {
          const data = res.map((country) => ({
            key: country.mercuryCountryName,
            value: country.mercuryCountryName,
            iso: country.iso2.toLowerCase(),
          }));
          setCountries(data);
        }
      })
      .catch(() => {
        message.error('Failed to trigger create invoice event');
      });
  };

  const items = [
    //version-1
    // {
    //   key: '/dashboard',
    //   icon: <Icon component={HomeFilled} />,
    //   label: <Link to="/dashboard">Dashboard</Link>,
    // },
    {
      key: '/transactions',
      icon: <ProfileFilled />,
      label: <Link to="/transactions">Transactions</Link>,
    },
    {
      key: '/invoice',
      icon: <FileTextFilled />,
      label: <Link to="/invoice">Invoice</Link>,
    },
    {
      key: '/payout',
      icon: <DollarCircleFilled />,
      label: <Link to="/payout">Payout</Link>,
    },
    {
      key: '/auditLog',
      icon: <AuditOutlined />,
      label: <Link to="/auditLog">Audit Log</Link>,
    },
  ];

  const footer_items = [
    {
      key: '/logout',
      icon: <LogoutOutlined />,
      label: (
        <a target="_blank" rel="noreferrer">
          Logout
        </a>
      ),
      theme: 'light',
    },
  ];

  const title = {
    '/dashboard': 'Dashboard',
    '/transactions': 'Transactions',
    '/invoice': 'Invoice',
    '/payout': 'Payout',
    '/auditLog': 'Audit Log',
  };

  const handleCollapse = (value) => {
    setCollapsed(value);
  };

  const handleLogout = (e) => {
    if (e.key === '/logout') {
      localStorage.clear();
      sessionStorage.clear();
      document.cookie.split(';').forEach((cookie) => {
        document.cookie = cookie
          .replace(/^ +/, '')
          .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
      });
      axios
        .post(BE_DOMAIN + '/logout', {
          withCredentials: true,
        })
        .then(() => {
          setAuthenticated(false);
        })
        .catch(() => {
          setAuthenticated(false);
        })
        .finally(() => {
          if (window.location.hostname !== 'localhost') {
            window.location.href = 'https://myapplications.microsoft.com/';
          }
        });
    }
  };

  const BatchJobs = () => {
    return (
      <CustomButton
        shape="round"
        size="middle"
        style={{ marginRight: '10px' }}
        variant="outlined"
        icon={<FolderOpenOutlined />}
        onClick={() => setIsAsyncTasksModalOpen(true)}
      >
        Batch Processes
      </CustomButton>
    );
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {!authenticated ? (
        <Spin fullscreen={true} spinning={true} />
      ) : (
        <>
          {notificationContextHolder}
          <AppFactsContext.Provider value={{ appFacts }}>
            <ActionContext.Provider value={{ setAction }}>
              <CountryContext.Provider value={{ countries }}>
                <PermissionsContext.Provider value={{ permissions }}>
                  <Sider theme="light" collapsed={collapsed}>
                    <div className="logo-div">
                      <Title className="nav-bar-logo" level={3}>
                        {collapsed ? 'CP' : 'CrowdPay'}
                      </Title>
                    </div>
                    <Divider orientationMargin={0} className="margin-zero" />
                    <div className="menu-switch">
                      {!collapsed ? (
                        <MenuFoldOutlined
                          className="menu-switch-icon-1"
                          onClick={() => handleCollapse(true)}
                        />
                      ) : (
                        <MenuUnfoldOutlined
                          className="menu-switch-icon"
                          onClick={() => handleCollapse(false)}
                        />
                      )}
                    </div>
                    <div
                      style={{
                        height: '82vh',
                        display: 'flex',
                        justifyContent: 'space-between',
                        flexDirection: 'column',
                      }}
                    >
                      <Menu
                        defaultSelectedKeys={['/dashboard']}
                        onSelect={(e) => setSelectedKey(e.key)}
                        selectedKeys={[selectedKey]}
                        items={items}
                        style={{ borderInlineEnd: 'none' }}
                      />
                      <Menu
                        onSelect={(e) => setSelectedKey(e.key)}
                        selectedKeys={[selectedKey]}
                        onClick={handleLogout}
                        items={footer_items}
                      />
                    </div>
                  </Sider>

                  <Layout>
                    <Header
                      className="header"
                      style={{
                        backgroundColor: Color.colorTextLightSolid,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                      }}
                    >
                      <Title className="title" level={3}>
                        {title[selectedKey]}
                      </Title>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {BatchJobs()}
                        {name && (
                          <Tooltip title={name}>
                            <Avatar
                              style={{
                                backgroundColor: Color.ProfileFilled,
                                color: Color.Avatar,
                              }}
                            >
                              {name.slice(0, 2).toUpperCase()}
                            </Avatar>
                          </Tooltip>
                        )}
                      </div>
                    </Header>
                    <Divider orientationMargin={0} className="header-margin" />
                    <Content className="container">
                      <Outlet />
                      <AsyncTasksModal
                        isOpen={isAsyncTasksModalOpen}
                        onClose={() => setIsAsyncTasksModalOpen(false)}
                      />
                    </Content>
                  </Layout>
                </PermissionsContext.Provider>
              </CountryContext.Provider>
            </ActionContext.Provider>
          </AppFactsContext.Provider>
        </>
      )}
    </Layout>
  );
};

export default Navigation;
